import { HttpError } from '@forto-dev/ui-platform-http'
import { useObserver } from 'mobx-react-lite'
import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useApi } from 'service/useApi'
import { useAuth } from 'service/useAuth'
import { logError } from 'utils/logger'

import { DocumentType } from '../domain/Document'

type DocumentTypeState = {
  documentTypes: DocumentType[]
}

export const DocumentTypeContext = createContext<DocumentTypeState | null>(null)

export const DocumentTypeContextProvider: FC<PropsWithChildren> = ({ children }) =>
  useObserver(() => {
    const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([])
    const {
      state: { authenticated },
    } = useAuth()
    const API = useApi()

    const fetchDocumentTypes = useCallback(async () => {
      try {
        const documentTypesFromApi = await API.documents.getDocumentTypesList()
        setDocumentTypes(documentTypesFromApi)
      } catch (e) {
        /** Temporary workaround to clean unauthorized errors from logs
         * @todo Move all error logging to HTTP service */
        if ((e as HttpError)?.status !== 401) {
          logError('Document Types could not be loaded')
        }
      }
    }, [])

    useEffect(() => {
      if (authenticated) {
        fetchDocumentTypes()
      }
    }, [authenticated])

    const providerValue = useMemo(() => ({ documentTypes }), [documentTypes])

    return <DocumentTypeContext.Provider value={providerValue}>{children}</DocumentTypeContext.Provider>
  })

export const useDocumentTypeState = (): DocumentTypeState => {
  const context = useContext(DocumentTypeContext)

  if (!context) {
    throw new Error('Must use useDocumentTypeState within a DocumentTypeContext')
  }

  return context
}
